import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  InputLabel,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";

const AiJobMatchingModal = ({
  open,
  onClose,
  onSubmit,
  defaultJobId = "",
  jobName = "",
  defaultTopK = 10,
  defaultFilterByLocation = false,
  defaultFilterByJobType = false,
  lockJobId = false,
}) => {
  const [jobId, setJobId] = useState("");
  const [topK, setTopK] = useState(10);
  const [filterByLocation, setFilterByLocation] = useState(false);
  const [filterByJobType, setFilterByJobType] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setJobId(defaultJobId !== undefined && defaultJobId !== null ? String(defaultJobId) : "");
    setTopK(defaultTopK);
    setFilterByLocation(!!defaultFilterByLocation);
    setFilterByJobType(!!defaultFilterByJobType);
    setErrors({});
  }, [
    open,
    defaultJobId,
    defaultTopK,
    defaultFilterByLocation,
    defaultFilterByJobType,
  ]);

  const CloseButton = useMemo(
    () =>
      styled(IconButton)(({ theme }) => ({
        position: "absolute",
        top: theme.spacing(1),
        right: theme.spacing(1),
      })),
    []
  );

  const validate = () => {
    const nextErrors = {};
    const parsedJobId = Number(jobId);
    const parsedTopK = Number(topK);

    if (!jobId || !Number.isInteger(parsedJobId) || parsedJobId <= 0) {
      nextErrors.jobId = "Job ID must be a positive number";
    }

    if (!Number.isInteger(parsedTopK) || parsedTopK < 1 || parsedTopK > 100) {
      nextErrors.topK = "Students number must be between 1 and 100";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    onSubmit?.({
      jobId: Number(jobId),
      topK: Number(topK),
      filterByLocation,
      filterByJobType,
    });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="popup" sx={{ maxWidth: 650 }}>
        <CloseButton onClick={onClose}>
          <CloseIcon />
        </CloseButton>

        <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
          AI Job Matching
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {lockJobId ? (
              <>
                <InputLabel sx={{ textAlign: "left" }}>Job *</InputLabel>
                <TextField
                  fullWidth
                  value={jobName || `Job #${jobId}`}
                  disabled
                />
              </>
            ) : (
              <>
                <InputLabel sx={{ textAlign: "left" }}>Job ID *</InputLabel>
                <TextField
                  fullWidth
                  placeholder="e.g. 257"
                  value={jobId}
                  onChange={(e) => setJobId(e.target.value)}
                  error={errors.jobId !== undefined}
                  helperText={errors.jobId}
                  type="number"
                />
              </>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <InputLabel sx={{ textAlign: "left" }}>
              Students number (Top K) *
            </InputLabel>
            <TextField
              fullWidth
              placeholder="e.g. 10"
              value={topK}
              onChange={(e) => setTopK(e.target.value)}
              error={errors.topK !== undefined}
              helperText={errors.topK}
              type="number"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filterByLocation}
                  onChange={(e) => setFilterByLocation(e.target.checked)}
                />
              }
              label="Filter by location"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filterByJobType}
                  onChange={(e) => setFilterByJobType(e.target.checked)}
                />
              }
              label="Filter by job type"
            />
          </Grid>

          <Grid item xs={12} className="btn-flex" sx={{ mt: 1 }}>
            <Button className="btn cancel" onClick={onClose} sx={{ color: "#fff" }}>
              Cancel
            </Button>
            <Button className="btn save" onClick={handleSubmit} sx={{ color: "#fff" }}>
              Match
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export default AiJobMatchingModal;
